/**
 * Dev mode authentication components
 *
 * These components provide Clerk-compatible interfaces for local development
 * without requiring a valid Clerk publishable key.
 *
 * In dev mode: SignedIn always renders children, SignedOut never renders
 * In production: These components are not used (import from @clerk/clerk-react instead)
 */

import type { ReactNode } from 'react';
import { User } from 'lucide-react';

// Check if we're in dev mode
export const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';

/**
 * SignedIn - Shows children only when signed in
 * In dev mode: always renders (user is always "signed in")
 */
export function SignedIn({ children }: { children: ReactNode }) {
  if (!isDevMode) {
    console.warn('DevModeAuth.SignedIn used in production mode - use @clerk/clerk-react instead');
    return null;
  }
  return <>{children}</>;
}

/**
 * SignedOut - Shows children only when signed out
 * In dev mode: never renders (user is always "signed in")
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function SignedOut(_props: { children: ReactNode }) {
  if (!isDevMode) {
    console.warn('DevModeAuth.SignedOut used in production mode - use @clerk/clerk-react instead');
    return null;
  }
  return null;
}

interface UserButtonProps {
  afterSignOutUrl?: string;
  appearance?: {
    elements?: Record<string, string>;
  };
}

/**
 * UserButton - Shows user avatar with menu
 * In dev mode: shows a simple dev user indicator
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function UserButton(_props: UserButtonProps) {
  if (!isDevMode) {
    console.warn('DevModeAuth.UserButton used in production mode - use @clerk/clerk-react instead');
    return null;
  }
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
      <User className="h-4 w-4" />
      <span>Dev Mode</span>
    </div>
  );
}
